import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { getAuthHeaders } from "../utils/api";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

/**
 * WelcomeOmen - The Astrum Oracle's Daily Message
 * Displays personalized cosmic wisdom in Soul Speak persona
 * Gold typography on obsidian background
 */
export const WelcomeOmen = ({ userName }) => {
  const [omen, setOmen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const fetchOmen = async () => {
      // Check if already dismissed this session
      const dismissedKey = `omen_dismissed_${new Date().toDateString()}`;
      if (sessionStorage.getItem(dismissedKey)) {
        setDismissed(true);
        setLoading(false);
        return;
      }

      try {
        // Build headers with auth token (PHASE 2B)
        const headers = await getAuthHeaders();
        
        const response = await fetch(`${API_URL}/api/oracle/welcome`, {
          method: "POST",
          headers,
          credentials: "include",
          body: JSON.stringify({ language_context: navigator.language || "en" })
        });

        if (response.ok) {
          const data = await response.json();
          setOmen(data);
        } else if (response.status === 401) {
          // Not authenticated - show nothing
          setDismissed(true);
        } else {
          setError("The Oracle rests...");
        }
      } catch (err) {
        console.error("Welcome omen fetch error:", err);
        setError("The cosmic connection wavers...");
      } finally {
        setLoading(false);
      }
    };

    fetchOmen();
  }, []);

  const handleDismiss = () => {
    const dismissedKey = `omen_dismissed_${new Date().toDateString()}`;
    sessionStorage.setItem(dismissedKey, "true");
    setDismissed(true);
  };

  // Don't render if dismissed or error
  if (dismissed || error) return null;

  // Loading state
  if (loading) {
    return (
      <motion.div
        className="px-4 pt-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <div className="relative rounded-xl overflow-hidden p-6" style={{ background: "linear-gradient(135deg, rgba(10,10,15,0.98) 0%, rgba(5,5,8,0.99) 100%)", border: "1px solid rgba(212, 175, 55, 0.15)" }}>
          <div className="flex items-center justify-center gap-3">
            <motion.div
              className="w-5 h-5 border-2 border-primary/30 border-t-primary rounded-full"
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            />
            <span className="text-primary/50 text-sm font-inter">The Oracle awakens...</span>
          </div>
        </div>
      </motion.div>
    );
  }

  if (!omen) return null;

  return (
    <AnimatePresence>
      {!dismissed && (
        <motion.div
          className="px-4 pt-4"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20, height: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div
            className="relative rounded-xl overflow-hidden"
            style={{
              background: "linear-gradient(135deg, rgba(10,10,15,0.98) 0%, rgba(5,5,8,0.99) 100%)",
              border: "1px solid rgba(212, 175, 55, 0.25)",
              boxShadow: "0 0 40px rgba(212, 175, 55, 0.08), inset 0 0 60px rgba(212, 175, 55, 0.03)"
            }}
            data-testid="welcome-omen"
          >
            {/* Decorative Corner Elements */}
            <div className="absolute top-3 left-3 w-6 h-6 border-t-2 border-l-2 border-primary/30" />
            <div className="absolute top-3 right-3 w-6 h-6 border-t-2 border-r-2 border-primary/30" />
            <div className="absolute bottom-3 left-3 w-6 h-6 border-b-2 border-l-2 border-primary/30" />
            <div className="absolute bottom-3 right-3 w-6 h-6 border-b-2 border-r-2 border-primary/30" />

            {/* Dismiss Button */}
            <button
              onClick={handleDismiss}
              className="absolute top-4 right-4 text-primary/30 hover:text-primary/60 transition-colors z-10"
              data-testid="dismiss-omen-btn"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Header */}
            <div className="text-center pt-6 pb-2">
              <span className="text-[9px] font-inter text-primary/40 uppercase tracking-[0.4em]">
                The Oracle Speaks
              </span>
            </div>

            {/* Main Content */}
            <div className="px-6 pb-6 space-y-5">
              {/* Cold Read - The Personal Hook */}
              {omen.cold_read && (
                <motion.p
                  className="text-center font-cinzel text-lg sm:text-xl leading-relaxed"
                  style={{ color: "#D4AF37" }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                >
                  {omen.cold_read}
                </motion.p>
              )}

              {/* The Mantra - Bold Cosmic Truth */}
              {omen.mantra && (
                <motion.div
                  className="text-center py-3 border-y border-primary/10"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                >
                  <p className="font-cinzel text-base sm:text-lg font-medium" style={{ color: "#F4E4B4" }}>
                    "{omen.mantra}"
                  </p>
                </motion.div>
              )}

              {/* The Path - Actionable Steps */}
              {omen.path && omen.path.length > 0 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.7 }}
                >
                  <h3 className="text-[10px] font-inter uppercase tracking-widest text-primary/50 mb-3">
                    Your Path Today
                  </h3>
                  <ul className="space-y-2">
                    {omen.path.map((step, i) => (
                      <motion.li
                        key={i}
                        className="flex gap-3 text-primary/70 text-sm font-inter"
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.8 + i * 0.15 }}
                      >
                        <span className="text-primary/40 font-cinzel">{i + 1}.</span>
                        <span>{step}</span>
                      </motion.li>
                    ))}
                  </ul>
                </motion.div>
              )}

              {/* Lucky Element */}
              {omen.lucky_element && (
                <motion.div
                  className="text-center pt-3"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1.2 }}
                >
                  <span className="text-[10px] font-inter text-primary/40 uppercase tracking-wider">
                    Your Talisman: <span className="text-primary/70 font-cinzel">{omen.lucky_element}</span>
                  </span>
                </motion.div>
              )}
            </div>

            {/* Footer Glow */}
            <div className="h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default WelcomeOmen;
